import { watch } from '@vue/composition-api';

/**
 * [data]
 * Definição das propriedades base das páginas.
 *
 * @since 1.0.0
 * @category data
 * @author Jorge Monteiro
 *
 */
const baseProps = {
    hasLoading: Boolean
};

/**
 * [data]
 * Definição da estrutura base das páginas de autenticação.
 * - Componentes
 * - Propriedades
 *
 * @since 1.0.0
 * @category data
 * @author Jorge Monteiro
 *
 */
const authenticationStructure = {
    components: {
        BaseForm: () => import( /* webpackChunkName: 'BaseForm' */ '@/components/bundles/layout/forms/BaseForm' ),
        BaseCard: () => import( /* webpackChunkName: 'BaseCard' */ '@/components/bundles/cards/BaseCard' ),
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' ),
        IconPadlock: () => import( /* webpackChunkName: 'IconPadlock' */ '@/components/bundles/layout/images/icons/IconPadlock' ),
        SubmissionHandler: () => import(
            /* webpackChunkName: 'SubmissionHandler' */
            '@/components/bundles/layout/SubmissionHandler/SubmissionHandler'
        )
    }
};

/**
 * [data]
 * Definição da estrutura base das páginas de listagens.
 * - Componentes
 * - Propriedades
 *
 * @since 1.0.0
 * @category data
 * @author Jorge Monteiro
 *
 */
const listStructure = {
    components: {
        BaseTable: () => import( /* webpackChunkName: 'BaseTable' */ '@/components/bundles/tables/BaseTable' )
    },
    props: {
        ...baseProps,
        list: Array,
        pagination: Object
    }
};

/**
 * [data]
 * Definição da estrutura base das páginas singulares.
 * - Componentes
 * - Propriedades
 *
 * @since 1.0.0
 * @category data
 * @author Jorge Monteiro
 *
 */
const singleStructure = {
    components: {
        BaseForm: () => import( /* webpackChunkName: 'BaseForm' */ '@/components/bundles/layout/forms/BaseForm' ),
        BaseCard: () => import( /* webpackChunkName: 'BaseCard' */ '@/components/bundles/cards/BaseCard' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField' */ '@/components/bundles/fields/BaseField' ),
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' )
    },
    props: {
        ...baseProps,
        model: {
            type: Object,
            default: () => ({})
        },
        isEdition: {
            type: Boolean,
            default: false
        },
        permissions: {
            type: Object,
            default: () => ({})
        }
    }
};

/**
 * [data]
 * Watch para quando a propriedade data é actualizada.
 * Quando isto acontece, é chamada uma função definida no componente, para que seja
 * feito o procedimento de estrutura da informação proveniente da API,
 * com o objectivo de tornar mais fácil de acesso a informação
 * no componente.
 *
 * @param {Object} props
 * @param {Function} structureFunction
 * @returns {Array}
 * @since 1.0.0
 * @category data
 * @author Jorge Monteiro
 *
 */
const useData = ( props, structureFunction ) => {
    watch( () => props.model,
        newData => {
            structureFunction( newData );
        }, {
            immediate: true
        });
};

export {
    authenticationStructure,
    listStructure,
    singleStructure,
    useData
};